import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_BASE } from '../api/client';

interface Worker { id: number; fio: string; position: string; numberOffice: number | null; phoneNumber: string; mail: string; imageS3?: string | null; }

export default function ContactsPage() {
  const { t } = useTranslation();
  const [workers, setWorkers] = useState<Worker[]>([]);

  useEffect(() => { axios.get(`${API_BASE}/contacts-workers`).then(r => setWorkers(r.data)).catch(() => {}); }, []);

  return (
    <div style={s.page}>
      <h1 style={s.pageTitle}>{t('contacts_title')}</h1>
      {workers.length > 0 && (
        <section style={{ marginBottom: 48 }}>
          <h2 style={s.sectionTitle}>{t('contacts_workers')}</h2>
          <div style={s.workersGrid}>
            {workers.map(w => (
              <div key={w.id} style={s.workerCard}>
                {w.imageS3 ? (
                  <img
                    src={`${API_BASE}/contacts-workers/get-image/${w.imageS3}`}
                    alt={w.fio}
                    style={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }}
                  />
                ) : (
                  <div style={s.workerAvatar}>{w.fio.charAt(0)}</div>
                )}
                <div style={s.workerInfo}>
                  <div style={s.workerName}>{w.fio}</div>
                  <div style={s.workerPosition}>{w.position}</div>
                  {w.numberOffice && <div style={s.workerDetail}>🏢 {t('contacts_office')} {w.numberOffice}</div>}
                  <div style={s.workerDetail}><a href={`tel:${w.phoneNumber}`} style={s.link}>📞 {w.phoneNumber}</a></div>
                  <div style={s.workerDetail}><a href={`mailto:${w.mail}`} style={s.link}>✉️ {w.mail}</a></div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
      <FeedbackForm />
    </div>
  );
}

function FeedbackForm() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', message: '' });
  const [file, setFile] = useState<File | null>(null);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFile(e.target.files?.[0] || null);
    e.target.value = '';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      fd.append('data', new Blob([JSON.stringify(form)], { type: 'application/json' }));
      if (file) fd.append('file', file);
      await axios.post(`${API_BASE}/feedback`, fd);
      setSent(true);
    } catch {} finally { setSending(false); }
  };

  if (sent) return <div style={s.successBox}>{t('contacts_success')}</div>;

  return (
    <div style={s.formSection}>
      <h2 style={s.formTitle}>{t('contacts_feedback_title')}</h2>
      <form onSubmit={handleSubmit} style={s.form}>
        <div style={s.formRow}>
          <input style={s.input} placeholder={t('contacts_name')} value={form.fullName} onChange={e => setForm({ ...form, fullName: e.target.value })} required />
          <input style={s.input} type="email" placeholder={t('contacts_email')} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required />
        </div>
        <input style={s.input} placeholder={t('contacts_phone')} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
        <textarea style={{ ...s.input, resize: 'vertical' }} placeholder={t('contacts_message')} rows={5} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} required />
        <div style={s.fileRow}>
          <label style={s.fileLabel}>
            {t('contacts_attach')}
            <input type="file" style={{ display: 'none' }} onChange={handleFileChange} />
          </label>
          {file && <span style={s.fileName}>{file.name}<button type="button" onClick={() => setFile(null)} style={s.removeFile}>×</button></span>}
        </div>
        <button style={s.submitBtn} type="submit" disabled={sending}>
          {sending ? t('contacts_sending') : t('contacts_send')}
        </button>
      </form>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  page: { maxWidth: 1000, margin: '0 auto', padding: 'clamp(24px, 5vw, 48px) clamp(16px, 4vw, 40px)' },
  pageTitle: { fontSize: 'clamp(1.5rem, 4vw, 2rem)', color: '#1a3a6b', marginBottom: 'clamp(20px, 3vw, 36px)' },
  link: { color: '#1a3a6b', textDecoration: 'none', fontWeight: 500 },
  formSection: { background: '#fff', borderRadius: 12, padding: 'clamp(20px, 4vw, 36px) clamp(16px, 4vw, 40px)', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  formTitle: { color: '#1a3a6b', fontSize: 'clamp(1.1rem, 3vw, 1.4rem)', marginBottom: 'clamp(16px, 3vw, 24px)' },
  form: { display: 'flex', flexDirection: 'column', gap: 14 },
  formRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))', gap: 14 },
  input: { padding: '12px 16px', borderRadius: 8, border: '1px solid #dde3ef', fontSize: '0.95rem', outline: 'none', width: '100%', maxWidth: '100%' },
  submitBtn: { padding: '13px 28px', background: '#1a3a6b', color: '#fff', border: 'none', borderRadius: 8, fontSize: '1rem', fontWeight: 600, cursor: 'pointer', alignSelf: 'flex-start', width: 'auto' },
  fileRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' as const },
  fileLabel: { display: 'inline-block', padding: '9px 18px', background: '#f0f4fb', border: '1px dashed #1a3a6b', borderRadius: 8, cursor: 'pointer', fontSize: '0.9rem', color: '#1a3a6b', fontWeight: 600 },
  fileName: { fontSize: '0.88rem', color: '#555', display: 'flex', alignItems: 'center', gap: 6 },
  removeFile: { background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '1.1rem', lineHeight: 1 },
  successBox: { background: '#f0fff4', border: '1px solid #27ae60', borderRadius: 10, padding: '20px 24px', color: '#27ae60', fontSize: '1rem', marginTop: 32 },
  sectionTitle: { color: '#1a3a6b', fontSize: 'clamp(1.1rem, 3vw, 1.4rem)', marginBottom: 'clamp(16px, 3vw, 20px)' },
  workersGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: 16 },
  workerCard: { background: '#fff', borderRadius: 10, padding: 'clamp(16px, 3vw, 20px)', display: 'flex', gap: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.08)', alignItems: 'flex-start' },
  workerAvatar: { width: 48, height: 48, borderRadius: '50%', background: '#1a3a6b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', fontWeight: 700, flexShrink: 0 },
  workerInfo: { flex: 1, minWidth: 0 },
  workerName: { fontWeight: 700, color: '#1a2a4a', fontSize: '0.95rem', marginBottom: 4, wordBreak: 'break-word' as const },
  workerPosition: { color: '#1a3a6b', fontSize: '0.85rem', fontWeight: 600, marginBottom: 8, wordBreak: 'break-word' as const },
  workerDetail: { fontSize: '0.85rem', color: '#555', marginBottom: 4, wordBreak: 'break-word' as const },
};
